import React, { useState, useCallback } from 'react';
import { apiClient } from '../../../api/client';

export interface GuestResult {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  chapterAffiliation: string | null;
  familyAssociation: string | null;
  seatCompositeId?: string | null;
  tableNumber?: number | null;
}

interface GuestSearchProps {
  onSelect: (guest: GuestResult) => void;
  placeholder?: string;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
  },
  searchRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  input: {
    flex: 1,
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
  },
  searchBtn: {
    padding: '0.5rem 0.85rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  results: {
    maxHeight: '280px',
    overflowY: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#fff',
  },
  resultItem: {
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    transition: 'background-color 0.1s',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  resultName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#0f2d1f',
  },
  resultMeta: {
    fontSize: '0.72rem',
    color: '#666',
  },
  resultSeat: {
    fontSize: '0.72rem',
    color: '#1565c0',
    fontWeight: 500,
  },
  noResults: {
    padding: '1rem',
    textAlign: 'center' as const,
    color: '#888',
    fontSize: '0.85rem',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.4rem 0.75rem',
    borderTop: '1px solid #e0e0e0',
    fontSize: '0.75rem',
    color: '#666',
  },
  pageBtn: {
    padding: '0.25rem 0.5rem',
    fontSize: '0.72rem',
    border: '1px solid #ccc',
    borderRadius: '3px',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  loading: {
    padding: '1rem',
    textAlign: 'center' as const,
    color: '#666',
    fontSize: '0.82rem',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.5rem',
  },
};

export function GuestSearch({ onSelect, placeholder = 'Search by name, ticket, chapter, or family...' }: GuestSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GuestResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);

  const search = useCallback(async (pageNum: number = 1) => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        query: query.trim(),
        page: String(pageNum),
        limit: '50',
      });
      const data = await apiClient<{ guests: GuestResult[]; total: number; page: number; totalPages: number }>(
        `/table-management/guests?${params.toString()}`
      );
      setResults(data.guests);
      setPage(data.page);
      setTotalPages(data.totalPages);
      setHasSearched(true);
    } catch {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      search(1);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.searchRow}>
        <input
          style={styles.input}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Search guests"
        />
        <button
          style={styles.searchBtn}
          onClick={() => search(1)}
          disabled={loading || !query.trim()}
          aria-label="Search"
        >
          Search
        </button>
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}

      {loading && <div style={styles.loading}>Searching...</div>}

      {!loading && hasSearched && results.length === 0 && (
        <div style={styles.noResults}>No guests found matching your search.</div>
      )}

      {!loading && results.length > 0 && (
        <div style={styles.results} role="listbox" aria-label="Search results">
          {results.map((guest) => (
            <div
              key={guest.id}
              style={styles.resultItem}
              onClick={() => onSelect(guest)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#fff';
              }}
              role="option"
              tabIndex={0}
              aria-label={`${guest.fullName}${guest.ticketNumber ? `, ticket ${guest.ticketNumber}` : ''}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(guest);
                }
              }}
            >
              <div>
                <div style={styles.resultName}>{guest.fullName}</div>
                <div style={styles.resultMeta}>
                  {[guest.ticketNumber, guest.chapterAffiliation, guest.familyAssociation]
                    .filter(Boolean)
                    .join(' • ')}
                </div>
              </div>
              {guest.seatCompositeId && (
                <span style={styles.resultSeat}>{guest.seatCompositeId}</span>
              )}
            </div>
          ))}

          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={styles.pageBtn}
                onClick={() => search(page - 1)}
                disabled={page <= 1}
              >
                ← Prev
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                style={styles.pageBtn}
                onClick={() => search(page + 1)}
                disabled={page >= totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
